import { projectRunway, type MonthSnapshot } from "@vulpari/core";
import { useMemo } from "react";

import {
  formatMoney,
  formatMonthLabel,
  formatMonthShort,
  formatSignedMoney,
} from "../lib/format.js";
import { useStore } from "../store.js";

const HORIZONS = [6, 12, 24] as const;

/** Chart geometry, in SVG user units. The viewBox scales to the container. */
const W = 820;
const H = 320;
const PAD = { top: 24, right: 24, bottom: 48, left: 84 };

export function Runway() {
  const nodes = useStore((state) => state.nodes);
  const flows = useStore((state) => state.flows);
  const context = useStore((state) => state.context);
  const horizon = useStore((state) => state.horizon);
  const setHorizon = useStore((state) => state.setHorizon);
  const ledger = useStore((state) =>
    state.ledgers.find((entry) => entry.id === state.currentLedgerId),
  );

  const currency = context.primaryCurrency ?? "GBP";
  // The runway is anchored to the ledger's opening-balance month when set, so
  // balances project from the right starting point; otherwise it follows the
  // month being viewed.
  const startMonth = ledger?.startMonth ?? context.month;
  const anchored = Boolean(ledger?.startMonth);

  const runway = useMemo(
    () =>
      projectRunway({
        nodes,
        flows,
        startMonth,
        horizon,
        primaryCurrency: currency,
        rates: context.rates,
      }),
    [nodes, flows, startMonth, horizon, currency, context.rates],
  );

  // Whether there is any spendable account to give the runway meaning.
  const hasSpendable = useMemo(
    () =>
      nodes.some(
        (node) =>
          node.type === "account" &&
          node.currency === currency &&
          (node.meta.role === "income_hub" || node.meta.role === "spending"),
      ),
    [nodes, currency],
  );

  const last = runway.snapshots[runway.snapshots.length - 1];

  return (
    <div className="h-full overflow-auto bg-canvas p-6">
      <div className="mx-auto flex max-w-4xl flex-col gap-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-ink">Runway</h1>
            <p className="mt-1 max-w-xl text-sm text-ink-muted">
              How your spendable cash moves month on month, carrying balances forward from{" "}
              {formatMonthLabel(startMonth)}
              {anchored ? " (your ledger's opening-balance month)" : ""}. One-offs land only in the
              month they fall due.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm text-ink-muted">
            Horizon
            <select
              className="figure rounded-lg border border-node-border bg-node-bg px-2 py-1.5 text-sm text-ink"
              value={horizon}
              onChange={(event) => setHorizon(Number(event.target.value))}
            >
              {HORIZONS.map((months) => (
                <option key={months} value={months}>
                  {months} months
                </option>
              ))}
            </select>
          </label>
        </div>

        {!hasSpendable ? (
          <p className="rounded-xl border border-node-border bg-node-bg px-4 py-3 text-sm text-ink-muted">
            Add an income hub or spending account to see your runway. Only spendable accounts in{" "}
            {currency} count towards the cash position.
          </p>
        ) : (
          <RunwayHeadline runway={runway} currency={currency} />
        )}

        <RunwayChart snapshots={runway.snapshots} currency={currency} />

        <RunwayTable snapshots={runway.snapshots} currency={currency} />

        {last ? (
          <p className="text-xs text-ink-muted">
            Projected through {formatMonthLabel(last.month)}. The lowest point across this window is{" "}
            <span className="figure text-ink">{formatMoney(runway.lowestLiquid, currency)}</span>.
          </p>
        ) : null}
      </div>
    </div>
  );
}

function RunwayHeadline({
  runway,
  currency,
}: {
  runway: ReturnType<typeof projectRunway>;
  currency: string;
}) {
  const last = runway.snapshots[runway.snapshots.length - 1];
  const onTrack = runway.shortfallMonth === null;

  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <div
        className={`rounded-xl border px-4 py-3 ${
          onTrack ? "border-positive/40 bg-positive/10" : "border-warning/40 bg-warning/10"
        }`}
      >
        <p className="text-xs uppercase tracking-wide text-ink-muted">Outlook</p>
        <p className="mt-1 text-sm font-medium text-ink">
          {onTrack
            ? "On track. Cash stays positive across this window."
            : runway.runwayMonths === 0
              ? `Short from the start, in ${formatMonthLabel(runway.shortfallMonth!)}.`
              : `Funded for ${runway.runwayMonths} month${runway.runwayMonths === 1 ? "" : "s"}, then short in ${formatMonthLabel(
                  runway.shortfallMonth!,
                )}.`}
        </p>
      </div>
      <div className="rounded-xl border border-node-border bg-node-bg px-4 py-3">
        <p className="text-xs uppercase tracking-wide text-ink-muted">Opening cash</p>
        <p className="figure mt-1 text-lg text-ink">
          {formatMoney(runway.openingLiquid, currency)}
        </p>
      </div>
      <div className="rounded-xl border border-node-border bg-node-bg px-4 py-3">
        <p className="text-xs uppercase tracking-wide text-ink-muted">Projected end</p>
        <p className="figure mt-1 text-lg text-ink">
          {last ? formatMoney(last.liquid, currency) : formatMoney(0, currency)}
        </p>
      </div>
    </div>
  );
}

function RunwayChart({ snapshots, currency }: { snapshots: MonthSnapshot[]; currency: string }) {
  const values = snapshots.map((snapshot) => snapshot.liquid);
  const maxV = Math.max(0, ...values);
  const minV = Math.min(0, ...values);
  const range = maxV - minV || 1;

  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const yOf = (value: number) => PAD.top + ((maxV - value) / range) * plotH;
  const zeroY = yOf(0);

  const band = plotW / Math.max(snapshots.length, 1);
  const barW = Math.min(band * 0.62, 48);

  // Label every bar when there is room, otherwise thin them out.
  const labelEvery = snapshots.length <= 12 ? 1 : snapshots.length <= 18 ? 2 : 3;

  return (
    <div className="rounded-2xl border border-node-border bg-node-bg p-4">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full"
        role="img"
        aria-label="Cash position by month"
      >
        {/* Top, zero and (if negative) bottom guide lines with figures. */}
        {[maxV, 0, minV < 0 ? minV : null].map((value, index) =>
          value === null ? null : (
            <g key={`grid-${index}`}>
              <line
                x1={PAD.left}
                x2={W - PAD.right}
                y1={yOf(value)}
                y2={yOf(value)}
                stroke="var(--colour-node-border)"
                strokeWidth={1}
                strokeDasharray={value === 0 ? "0" : "4 4"}
              />
              <text
                x={PAD.left - 10}
                y={yOf(value) + 4}
                textAnchor="end"
                className="figure"
                fontSize={12}
                fill="var(--colour-text-muted)"
              >
                {formatMoney(value, currency)}
              </text>
            </g>
          ),
        )}

        {snapshots.map((snapshot, index) => {
          const x = PAD.left + band * index + band / 2;
          const positive = snapshot.liquid >= 0;
          const y = positive ? yOf(snapshot.liquid) : zeroY;
          const height = Math.abs(yOf(snapshot.liquid) - zeroY);
          const colour = positive ? "var(--colour-positive)" : "var(--colour-warning)";
          return (
            <g key={snapshot.month}>
              <rect
                x={x - barW / 2}
                y={y}
                width={barW}
                height={Math.max(height, 1)}
                rx={4}
                fill={colour}
                opacity={0.85}
              >
                <title>
                  {formatMonthLabel(snapshot.month)}: {formatMoney(snapshot.liquid, currency)}
                </title>
              </rect>
              {index % labelEvery === 0 ? (
                <text
                  x={x}
                  y={H - PAD.bottom + 18}
                  textAnchor="middle"
                  fontSize={11}
                  fill="var(--colour-text-muted)"
                >
                  {formatMonthShort(snapshot.month)}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function RunwayTable({ snapshots, currency }: { snapshots: MonthSnapshot[]; currency: string }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-node-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-node-bg text-left text-xs uppercase tracking-wide text-ink-muted">
            <th className="px-4 py-2 font-medium">Month</th>
            <th className="px-4 py-2 text-right font-medium">Change</th>
            <th className="px-4 py-2 text-right font-medium">Cash position</th>
            <th className="px-4 py-2 font-medium">Notes</th>
          </tr>
        </thead>
        <tbody>
          {snapshots.map((snapshot, index) => (
            <tr key={snapshot.month} className={index % 2 === 0 ? "bg-canvas" : "bg-node-bg/40"}>
              <td className="px-4 py-2 text-ink">{formatMonthLabel(snapshot.month)}</td>
              <td
                className={`figure px-4 py-2 text-right ${
                  snapshot.netChange < 0 ? "text-warning" : "text-ink-muted"
                }`}
              >
                {snapshot.netChange >= 0 ? "+" : ""}
                {formatSignedMoney(snapshot.netChange, currency)}
              </td>
              <td
                className={`figure px-4 py-2 text-right ${
                  snapshot.liquid < 0 ? "text-warning" : "text-ink"
                }`}
              >
                {formatMoney(snapshot.liquid, currency)}
              </td>
              <td className="px-4 py-2 text-xs text-ink-muted">
                {snapshot.warnings.length > 0
                  ? snapshot.warnings.map((warning) => warning.message).join(" ")
                  : ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
